/**
 * Per-row popover trigger that hosts the notes editor and (for recipient trees)
 * the recipient settings strip.
 *
 * Controlled-open: the parent (`NodeRow`) owns `open` state and passes
 * `onOpenChange`; this component is purely presentational regarding visibility.
 * Owns: the in-progress new-note draft, the relative-time formatting of
 * existing notes, and the resolution of the current user's display name from
 * UserContext. When there are no notes on the node, the trigger button is
 * rendered transparent so the row layout stays stable (matches the
 * pre-refactor behavior); `aria-hidden` is applied in that state so assistive
 * tech skips the invisible button (the menu's "Add Note" entry remains the
 * canonical reach-in path).
 */
import { FC, useContext, useMemo, useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type {
  NodeExtraInformation,
  RecipientEmailConfig,
} from '#types/System.RelationshipTree.types';
import { UserContext } from '@/providers/UserContext';
import NotesPopoverContent from './NotesPopoverContent';

dayjs.extend(relativeTime);

const formatTimestamp = (timestamp: Timestamp): string =>
  dayjs(timestamp.toDate()).fromNow();

type NotesPopoverProps = {
  extraInformation?: NodeExtraInformation;
  onExtraInfoChange?: (next: NodeExtraInformation) => void;
  disabled?: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  showRecipientControls?: boolean;
  recipientConfig?: RecipientEmailConfig;
  onRecipientConfigChange?: (next: RecipientEmailConfig) => void;
  resolvedEmail?: string | null;
  ariaLabel?: string;
};

const NotesPopover: FC<NotesPopoverProps> = ({
  extraInformation,
  onExtraInfoChange,
  disabled = false,
  open,
  onOpenChange,
  showRecipientControls = false,
  recipientConfig,
  onRecipientConfigChange,
  resolvedEmail,
  ariaLabel,
}) => {
  const { user, users } = useContext(UserContext);
  const [newNote, setNewNote] = useState('');

  const currentUserData = users?.find((u) => u.docId === user?.uid);

  const notes = extraInformation?.notes ?? [];
  const hasNotes = notes.length > 0;

  const mostRecentNoteTooltip = useMemo(() => {
    if (!hasNotes) return 'No notes';
    const latestNote = notes[notes.length - 1];
    const maxLength = 100;
    const noteText =
      latestNote.text.length > maxLength
        ? `${latestNote.text.substring(0, maxLength)}...`
        : latestNote.text;
    const timeAgo =
      latestNote.timestamp instanceof Timestamp
        ? formatTimestamp(latestNote.timestamp)
        : '';
    return `${noteText} - ${latestNote.userName} · ${timeAgo}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNotes, extraInformation?.notes]);

  const handleAddNote = (): void => {
    if (!newNote.trim() || !onExtraInfoChange) return;
    const updatedNotes = [
      ...(extraInformation?.notes ?? []),
      {
        text: newNote.trim(),
        userName:
          currentUserData?.name ||
          user?.displayName ||
          user?.email ||
          'Unknown User',
        timestamp: Timestamp.now(),
      },
    ];
    onExtraInfoChange({ ...extraInformation, notes: updatedNotes });
    setNewNote('');
  };

  const handleDeleteNote = (indexToDelete: number): void => {
    if (!onExtraInfoChange) return;
    const updatedNotes = (extraInformation?.notes ?? []).filter(
      (_, index) => index !== indexToDelete,
    );
    onExtraInfoChange({ ...extraInformation, notes: updatedNotes });
  };

  const buttonElement = (
    <Button
      type="text"
      icon={<MessageOutlined />}
      aria-label={ariaLabel ?? 'notes-button'}
      aria-hidden={!hasNotes}
      tabIndex={hasNotes ? 0 : -1}
      style={{ color: hasNotes ? 'inherit' : 'transparent' }}
    />
  );

  const recipientControls =
    showRecipientControls && onRecipientConfigChange
      ? {
          role: recipientConfig?.role,
          enabled: recipientConfig?.enabled,
          resolvedEmail: resolvedEmail ?? null,
          onRoleChange: (role: 'to' | 'cc' | 'bcc') =>
            onRecipientConfigChange({
              role,
              enabled: recipientConfig?.enabled ?? true,
            }),
          onEnabledChange: (enabled: boolean) =>
            onRecipientConfigChange({
              role: recipientConfig?.role ?? 'to',
              enabled,
            }),
        }
      : undefined;

  return (
    <Popover
      content={
        <NotesPopoverContent
          notes={notes}
          newNote={newNote}
          onNewNoteChange={setNewNote}
          onAddNote={handleAddNote}
          onDeleteNote={handleDeleteNote}
          formatTimestamp={formatTimestamp}
          disabled={disabled}
          recipientControls={recipientControls}
        />
      }
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      placement="bottom"
    >
      {hasNotes ? (
        <Tooltip title={mostRecentNoteTooltip} placement="top">
          {buttonElement}
        </Tooltip>
      ) : (
        buttonElement
      )}
    </Popover>
  );
};

export default NotesPopover;
